import {
  CreateButton,
  ReferenceArrayInput,
  required,
  SimpleForm,
  TextField,
  TopToolbar,
  useRecordContext,
  useTranslate,
  WrapperField,
} from 'react-admin';
import { Datagrid, IfCanAccess } from '@react-admin/ra-rbac';
import { useNavigate } from 'react-router-dom';
import { ListLive } from '@react-admin/ra-realtime';
import { AssetRef } from '@x-guard/xgac-types/xgac';
import { Typography, Stack } from '@mui/material';
import { LIST_DEBOUNCE } from '../../../config';
import { DateFieldWithTime } from '../../../components/fields/DateFieldWithTime';
import { AssetsInDialogButton } from '../../../components/buttons/assetsInDialogButton';
import SearchFields from '../components/fields/Filters';
import { StabilityAlert, StabilityLevel } from '../../../components/StabilityAlert';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { GrayAutocompleteArrayInputWithUpperLabel } from '../components/inputs/GrayAutocompleteArrayInputWithUpperLabel';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { AssetGroupDeleteButton } from '../../../components/buttons/assetGroupDeleteButton';
import {
  BhvkSaveButton,
  BhvkBackButton,
  BhvkTableEditButton,
} from '../buttons/ActionButtons';
import { PageContentCard } from '../cards/PageContentCard';
import { FormToolbar } from '../components/FormToolbar';

const transform = (data: any) => {

  return {
    ...data,
    assets: data.assets.map((record: string) => ({ _ref: 'Asset', _id: record } as AssetRef)),
  };

};

export const GroupList = () => {

  const translate = useTranslate();
  return (
    <PageContentCard>
      <h3>{translate('menu.items.groups')}</h3>
      <ListLive
        filters={SearchFields} debounce={LIST_DEBOUNCE} title="menu.items.groups"
        actions={<TopToolbar><CreateButton size="medium" variant="contained"/></TopToolbar>}
        sort={{ field: 'name', order: 'ASC' }}
        sx={{ '& > .RaList-main > .RaList-content': { mt: '12px' } }}
      >
        <>
          <Datagrid rowClick={false} bulkActionButtons={false}>
            <TextField source="name" label="general.fields.name"/>
            <AssetsInDialogButton
              variant="bhvk"
              resource="asset-groups"
              source="assets"
              label="resources.asset-groups.fields.bhvk_count"
              buttonLabel={'resources.asset-groups.fields.bhvk_count_button'}
              className="BhvkCompactContainedButton-root"
              closeClassName="BhvkCancelButton-root"
            />
            <DateFieldWithTime source="createdAt" label="general.fields.createdAt" timeOnHover={true}/>
            <DateFieldWithTime source="updatedAt" label="general.fields.updatedAt" timeOnHover={true}/>
            <WrapperField label="" sortable={false} source="actions">
              <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ whiteSpace: 'nowrap', py: 1 }}>
                <IfCanAccess action="edit" resource="asset-groups">
                  <BhvkTableEditButton />
                </IfCanAccess>
                <IfCanAccess action="delete" resource="asset-groups">
                  <AssetGroupDeleteButton className="BhvkTableActionButton-root BhvkTableActionButton-destructive" />
                </IfCanAccess>
              </Stack>
            </WrapperField>
          </Datagrid>
        </>
      </ListLive>
    </PageContentCard>
  );

};

export const GroupCreate = () => {

  const translate = useTranslate();
  const navigate = useNavigate();

  return (
    <CreateWithSuccess
      component={PageContentCard}
      transform={transform}
      title="menu.items.groups"
      actions={false}
    >
      <StabilityAlert stability={StabilityLevel.Stable} />
      <h4>{translate('resources.asset-groups.text.create_title')}</h4>
      <Typography variant="body1">
        {translate('resources.groups.text.bhvk-explanation')}
      </Typography>
      <hr />
      <SimpleForm
        toolbar={(
          <FormToolbar sx={{ maxWidth: '500px' }}>
            <BhvkBackButton
              onClick={() => navigate('/asset-groups', { replace: true })}
            />
            <BhvkSaveButton />
          </FormToolbar>
        )}
        sx={{ maxWidth: '1000px' }}
      >
        <Stack spacing={1} sx={{ width: '500px' }}>
          <GrayTextInputWithUpperLabel
            source="name"
            validate={required()}
            label="general.fields.name"
          />
          <ReferenceArrayInput source="assets" reference="all-assets" fullWidth>
            <GrayAutocompleteArrayInputWithUpperLabel
              fullWidth
              label="general.text.buttons_or_persons"
            />
          </ReferenceArrayInput>
        </Stack>
      </SimpleForm>
    </CreateWithSuccess>
  );

};

const GroupEditHeader = () => {

  const translate = useTranslate();
  const record = useRecordContext();

  return (
    <>
      <h4 style={{ marginBottom: '10px' }}>
        "{record?.name}" {translate('general.text.settings')}
      </h4>
      <Typography variant="body1">
        {translate('resources.groups.text.bhvk-explanation')}
      </Typography>
      <hr />
    </>
  );

};

const GroupEditFields = () => (
  <Stack spacing={1} sx={{ width: '500px' }}>
    <GrayTextInputWithUpperLabel
      source="name"
      validate={required()}
      label="general.fields.name"
    />
    <ReferenceArrayInput source="assets" reference="all-assets" fullWidth>
      <GrayAutocompleteArrayInputWithUpperLabel
        fullWidth
        label="general.text.buttons_or_persons"
      />
    </ReferenceArrayInput>
  </Stack>
);

export const GroupEdit = () => {

  const navigate = useNavigate();

  return (
    <EditWithSuccess
      component={PageContentCard}
      transform={transform}
      title="menu.items.groups"
      actions={false}
    >
      <GroupEditHeader />
      <SimpleForm
        toolbar={(
          <FormToolbar sx={{ maxWidth: '500px' }}>
            <BhvkBackButton
              onClick={() => navigate('/asset-groups', { replace: true })}
            />
            <BhvkSaveButton />
          </FormToolbar>
        )}
        sx={{ maxWidth: '1000px' }}
      >
        <GroupEditFields />
      </SimpleForm>
    </EditWithSuccess>
  );

};
